import h from "trader-hyperscript";
import { rpc } from "trader.ts/network";
import { Source } from "trader.ts/types/source";
import {
  Field,
  Form,
  checkBox,
  constantField,
  object_keys,
  textBox,
} from "trader.ts/ui.forms";
import {
  messagetemplateid,
  messagetemplatetype,
} from "../../../sql/classy/types";
import * as style from "../../../styles/style.css.classes";
import { constantFieldWithRender } from "../../client/constantfieldwithrender";
import { renderPopupModal } from "../../client/popup";
import { renderDeleteButton, renderSaveButton } from "../../client/savebutton";
import { applyPlaceholders } from "../../domain/emailtemplates/emailtemplates";
import { makeEmailWithNewTemplate } from "../../domain/emailtemplates/newemailstyles";
import * as routes from "../../routes";
import currency from "currency.js";
import { isNil } from "../../lib/istype";
import { renderCurrency } from "../../render";

export function render(opts: {
  schoolname: string;
  always_show_wire_instructions: boolean;
  has_connection_to_accounting_software: boolean;
  has_electronic_payments: boolean;
  school_account_number: string;
  periodfee_amount: null | currency;
  is_demo: boolean;
  schoolurl: string;
  invoice_details: string;
  template: {
    id: messagetemplateid | null;
    template_type: messagetemplatetype;
    is_default: boolean;
    name: string | null;
    title: string;
    html: string;
  };
  defaultTemplate: {
    title: string;
    body: string;
    placeholders: Record<string, string>;
    placeholder_help?: Record<string, string>;
  };
}): HTMLElement | HTMLElement[] {
  const extraPlaceHolders = {
    schoolname: opts.schoolname,
    payment_via_accounting_software: opts.has_connection_to_accounting_software
      ? "true"
      : "",
    electronic_payment_link:
      opts.has_electronic_payments || opts.is_demo
        ? "https://www.google.com"
        : "",
    wire_payment:
      opts.has_electronic_payments ||
      opts.has_connection_to_accounting_software ||
      opts.is_demo
        ? ""
        : "true",
    periodfee_amount: isNil(opts.periodfee_amount)
      ? ""
      : renderCurrency(opts.periodfee_amount),
    periodfee_is_paid: "",
    always_show_wire_instructions: opts.always_show_wire_instructions
      ? "true"
      : "",
    school_account_number: opts.school_account_number,
    schoolurl: opts.schoolurl,
    invoice_details: opts.invoice_details,
    created_by_admin_or_teacher:
      opts.template.template_type === "EnrollmentCreatedPrivate" ||
      opts.template.template_type === "EnrollmentCreatedGroup" ||
      opts.template.template_type === "EnrollmentCreatedCamp"
        ? "admin"
        : "",
    enrollmentrequest_approved: "",
  };

  const showDefaultPopupS = new Source(false);
  const showPlaceholderHelperPopupS = new Source(false);

  const defaultTemplate = opts.defaultTemplate;

  const templ = opts.template;

  const form = new Form({})
    .addField("name", [], () =>
      textBox({
        initialVal: templ.name || "",
        label: "Naam sjabloon",
      }),
    )
    .addField("is_default", [], () =>
      checkBox({
        initialVal: templ.is_default,
        label: "Is standaard sjabloon",
        class: style.checkbox + style.input_like,
      }),
    )
    .addField("title", [], () =>
      isSmsTemplate(opts.template.template_type)
        ? constantField("")
        : textBox({ initialVal: templ.title, label: "Titel" }),
    )
    .addField("html", [], () => {
      const lines = templ.html.split("\n");
      return textBox({
        initialVal: templ.html,
        label: "Sjabloon",
        type: "textarea",
        style: `height: ${Math.max(lines.length * 1.25, 30)}rem`,
      });
    })
    .addField(
      "example_title",
      ["title"],
      ({ title }): Promise<Field<number>> => {
        const replaced = applyPlaceholders(
          defaultTemplate,
          { title, html: "" },
          {
            ...defaultTemplate.placeholders,
            ...extraPlaceHolders,
          },
        );
        return isSmsTemplate(opts.template.template_type)
          ? constantField(0)
          : constantFieldWithRender(
              0,
              <div>
                <label>{"Voorbeeld titel"}</label>
                <div
                  className={style.input_like}
                  style="background-color: transparent"
                >
                  {replaced.title}
                </div>
              </div>,
            );
      },
    )
    .addField("example_html", ["html"], ({ html }): Promise<Field<number>> => {
      const replaced = applyPlaceholders(
        defaultTemplate,
        { title: "", html },
        {
          ...defaultTemplate.placeholders,
          ...extraPlaceHolders,
        },
      );

      return constantFieldWithRender(
        0,
        (() => {
          if (isSmsTemplate(opts.template.template_type)) {
            const sms = (replaced.body || "").trim();
            return (
              <div>
                <label>{"Voorbeeld SMS"}</label>
                <pre>{sms}</pre>
                <div style="text-align: right;">
                  {stringToJsxClientside(
                    "Voorbeeldbericht bevat <b>%n</b> letters.".replace("%n", sms.length.toString()),
                  )}
                  <span style="font-size: 0.85rem;">
                    {"Bevat je bericht meer dan 160 letters, dan zullen er twee berichten worden uitgestuurd."}
                  </span>
                </div>
              </div>
            );
          } else {
            const container = <div></div>;
            const shadow = container.attachShadow({ mode: "closed" });

            const parsed = makeEmailWithNewTemplate(
              opts,
              stringToJsxClientside(replaced.body),
            );

            Array.from(parsed.getElementsByTagName("style")).forEach((st) => {
              shadow.appendChild(st);
            });
            shadow.appendChild(parsed.getElementsByTagName("body")[0]);

            return (
              <div>
                <label>{"Voorbeeld email"}</label>
                {container}
              </div>
            );
          }
        })(),
      );
    })
    .build({
      render: (prerenderd) => (
        <div>
          <section
            className={style.equal_columns}
            style="align-items: flex-end;"
          >
            {prerenderd.name()}
            {prerenderd.is_default()}
          </section>

          <div className={style.equal_columns}>
            <div>{prerenderd.title()}</div>
            <div>{prerenderd.example_title()}</div>
          </div>

          <div className={style.equal_columns}>
            <div>{prerenderd.html()}</div>
            <div style="position: sticky; top: 8px">
              {prerenderd.example_html()}
            </div>
          </div>
        </div>
      ),
    });

  function renderDefaultPopup({ close }: { close: () => void }) {
    const currForm = form.s.get();
    if (currForm.tag === "parsed") {
      const defaultTempl = opts.defaultTemplate;
      const replaced = applyPlaceholders(
        defaultTempl,
        { title: defaultTempl.title, html: defaultTempl.body },
        {
          ...defaultTempl.placeholders,
          ...extraPlaceHolders,
        },
      );

      const container = <div></div>;
      const shadow = container.attachShadow({ mode: "closed" });
      shadow.appendChild(
        makeEmailWithNewTemplate(opts, stringToJsxClientside(replaced.body)),
      );

      return (
        <div className={style.panel + style.reset_text}>
          <div className={style.equal_columns}>
            <input type="text" readonly value={defaultTempl.title} />
            <div
              className={style.input_like}
              style="background-color: transparent"
            >
              {replaced.title}
            </div>
          </div>
          <div className={style.equal_columns}>
            <textarea
              readonly
              value={defaultTempl.body}
              style={`height: ${Math.max(
                defaultTempl.body.split("\n").length * 1.25,
                30,
              )}rem`}
            />
            <div style="position: sticky; top: 8px">{container}</div>
          </div>
          <div style="text-align: right">
            <button onclick={close}>{"Sluit"}</button>
          </div>
        </div>
      );
    } else {
      return <span></span>;
    }
  }

  function renderPlaceholderHelperPopup({ close }: { close: () => void }) {
    const currForm = form.s.get();
    if (currForm.tag === "parsed") {
      const defaultTempl = opts.defaultTemplate;
      const relevantPlaceholderKeys = Object.keys(defaultTempl.placeholders);
      const extraRelevantPlaceholders = Object.fromEntries(
        Object.entries(extraPlaceHolders).filter(([key, _val]) =>
          relevantPlaceholderKeys.includes(key),
        ),
      );
      const pls = {
        ...defaultTempl.placeholders,
        ...extraRelevantPlaceholders,
      };

      return (
        <div
          style="white-space: pre-line;"
          className={style.panel + style.reset_text}
        >
          {object_keys(pls).map((key) => {
            const help =
              ("placeholder_help" in defaultTempl &&
                defaultTempl.placeholder_help &&
                defaultTempl.placeholder_help[key]) ||
              null;
            return (
              <div>
                <b>{key}</b>: {pls[key]}
                {help === null ? (
                  ""
                ) : (
                  <span style="font-style: italic">
                    <br />
                    {help}
                  </span>
                )}
              </div>
            );
          })}
          <div style="text-align: right">
            <button onclick={close}>{"Sluit"}</button>
          </div>
        </div>
      );
    } else {
      return <span></span>;
    }
  }

  return (
    <div>
      <div>
        <section>
          {form.render()}

          {renderPopupModal(showDefaultPopupS, renderDefaultPopup)}

          {renderPopupModal(
            showPlaceholderHelperPopupS,
            renderPlaceholderHelperPopup,
          )}

          <div style="text-align: right">
            <button onclick={() => showDefaultPopupS.set(true)}>
              {"Toon standaard sjabloon"}
            </button>
            <button onclick={() => showPlaceholderHelperPopupS.set(true)}>
              {"Toon variabelen"}
            </button>
            {opts.template.id !== null
              ? renderDeleteButton({
                  label: "Verwijder sjabloon",
                  confirmationLabel: "Ben je zeker dat je dit sjabloon wilt verwijderen?",
                  doDelete: () =>
                    rpc(routes.deleteEmailTemplateSpec, undefined, {
                      id: opts.template.id!,
                    }),
                  redirectTo: routes.editemailtemplates_listRoute.link(),
                })
              : []}
            {renderSaveButton({
              save: async function (vals) {
                return rpc(routes.emailTemplateSaveR, undefined, {
                  id: opts.template.id,
                  is_default: vals.is_default,
                  templateType: opts.template.template_type,
                  title: vals.title,
                  html: isSmsTemplate(opts.template.template_type)
                    ? (vals.html || "").trim()
                    : // The browser parser is more lenient than the nodejs parser.
                      // This way we make (more) sure what is shown on the example will be sent to the students
                      stringToJsxClientside(vals.html).outerHTML,
                  name: vals.name,
                });
              },
              text: {
                normal: "Sla op",
                success: "Opgeslagen!",
              },
              timeout: 1500,
              reload: () =>
                (document.location =
                  routes.editemailtemplates_listRoute.link()),
              formS: form.s,
            })}
          </div>
        </section>
      </div>
    </div>
  );
}

function isSmsTemplate(type: messagetemplatetype): boolean {
  if (type.includes("SMS")) {
    return true;
  } else {
    return false;
  }
}

export function stringToJsxClientside(html: string): HTMLElement {
  const domParser = new DOMParser();
  return domParser.parseFromString(
    `<html><body>${html}</body></html>`,
    "text/html",
  ).body;
}
